// Featured Events upgrade — admin additional-photos manager.
//
// Lets admins upload, view, reorder and remove the additional event photos
// that appear (after the primary flyer) in the detail-page carousel.
// Reuses the shared event-flyers bucket via /api/events/{kind}/{id}/photos.

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, Trash, ArrowUp, ArrowDown, Images } from '@phosphor-icons/react';
import { eventMediaAPI } from '../../lib/eventMediaApi';

const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 12 * 1024 * 1024;

export default function EventPhotoManager({ kind, eventId, disabled = false }) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    if (!kind || !eventId) return;
    eventMediaAPI.listPhotos(kind, eventId)
      .then((r) => setPhotos(r.data?.photos || []))
      .catch(() => setPhotos([]));
  };
  useEffect(load, [kind, eventId]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let last = null;
    try {
      for (const file of list) {
        if (!ALLOWED.includes(file.type)) { toast.error(`${file.name}: use JPG, PNG or WEBP.`); continue; }
        if (file.size > MAX_BYTES) { toast.error(`${file.name} is over 12 MB.`); continue; }
        last = await eventMediaAPI.addPhoto(kind, eventId, file);
      }
      if (last?.data?.photos) setPhotos(last.data.photos);
      toast.success('Photos updated.');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Upload failed.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const move = async (from, to) => {
    if (to < 0 || to >= photos.length) return;
    const next = photos.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setPhotos(next); // optimistic
    try {
      const r = await eventMediaAPI.reorder(kind, eventId, next.map((p) => p.id));
      if (r.data?.photos) setPhotos(r.data.photos);
    } catch {
      toast.error('Could not reorder.');
      load();
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await eventMediaAPI.deletePhoto(photoId);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
      toast.success('Photo removed.');
    } catch {
      toast.error('Could not remove photo.');
    }
  };

  return (
    <div data-testid="event-photo-manager" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[13px] font-medium text-[#013523]">
          <Images size={16} weight="duotone" /> Additional photos
          <span className="text-[#6a766e] font-normal">({photos.length})</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="sr-only"
          data-testid="event-photo-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          data-testid="event-photo-add-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#013523] text-white text-xs min-h-[40px] disabled:opacity-50"
        >
          <UploadSimple size={14} /> {busy ? 'Uploading…' : 'Add photos'}
        </button>
      </div>

      {photos.length === 0 ? (
        <div
          data-testid="event-photo-empty"
          className="border border-dashed border-[#e3dcd1] rounded-xl bg-[#faf7f0] px-4 py-5 text-center text-[12px] text-[#6a766e]"
        >
          No additional photos yet. The primary flyer stays the cover; photos you add here appear after it in the gallery.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div
              key={p.id}
              data-testid={`event-photo-item-${i}`}
              className="relative group rounded-lg overflow-hidden border border-[#e3dcd1] bg-[#f7f4ed] aspect-square"
            >
              <img src={p.url} alt="" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/45 backdrop-blur-sm px-1 py-1 transition-opacity">
                <button type="button" aria-label="Move left" disabled={i === 0} onClick={() => move(i, i - 1)} data-testid={`event-photo-left-${i}`} className="text-white disabled:opacity-30 p-1 min-w-[32px] min-h-[32px] flex items-center justify-center"><ArrowUp size={14} className="rotate-[-90deg]" /></button>
                <button type="button" aria-label="Remove photo" onClick={() => remove(p.id)} data-testid={`event-photo-remove-${i}`} className="text-white p-1 min-w-[32px] min-h-[32px] flex items-center justify-center"><Trash size={14} /></button>
                <button type="button" aria-label="Move right" disabled={i === photos.length - 1} onClick={() => move(i, i + 1)} data-testid={`event-photo-right-${i}`} className="text-white disabled:opacity-30 p-1 min-w-[32px] min-h-[32px] flex items-center justify-center"><ArrowDown size={14} className="rotate-[-90deg]" /></button>
              </div>
              <span className="absolute top-1 left-1 text-[10px] font-semibold bg-white/90 text-[#013523] rounded-full px-1.5">{i + 1}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
